import type { ZudokuConfig } from "zudoku";
import { getActiveBrand } from "./lib/branding/resolver.ts";
import { zudokuNavigation } from "./config/navigation.ts";
import { SITE_URLS } from "./config/site.ts";
import { HostnameBrandSync } from "./lib/branding/HostnameBrandSync.tsx";
import { getZudokuThemeConfig } from "./lib/branding/theme.ts";
import { resolveBrandVisual } from "./lib/branding/placeholder.ts";
import { Badge } from "./lib/mdx/Badge.tsx";
import { Table } from "./lib/mdx/Table.tsx";
import { Tabs, Tab } from "./lib/mdx/Tabs.tsx";

const activeBrand = getActiveBrand();

const config: ZudokuConfig = {
  site: {
    title: activeBrand.metadata.title,
    logo: {
      src: {
        light: resolveBrandVisual(activeBrand, "logo"),
        dark: resolveBrandVisual(activeBrand, "logo"),
      },
      alt: activeBrand.displayName,
      width: "140px",
    },
    showPoweredBy: false,
    footer: activeBrand.footer.columns
      ? {
          columns: activeBrand.footer.columns,
          copyright: activeBrand.footer.copyright,
        }
      : {
          columns: [
            {
              title: "",
              links: [{ label: activeBrand.footer.copyright || "2026 Zorya Telecom by Everlay Group", href: activeBrand.footer.website || "https://www.everlay.com/" }],
            },
          ],
        },
  },
  metadata: {
    title: activeBrand.metadata.title,
    description: activeBrand.metadata.description,
    favicon: resolveBrandVisual(activeBrand, "favicon"),
  },
  header: {
    navigation: [
      {
        label: "Go to the Portal",
        to: activeBrand.contentOverrides?.portalUrl ?? SITE_URLS.portal,
        target: "_blank",
      },
      {
        label: "Sign Up",
        to: activeBrand.contentOverrides?.registerUrl ?? SITE_URLS.register,
        target: "_blank",
      },
    ],
    themeSwitcher: { enabled: false },
  },
  mdx: {
    components: { Badge, Table, Tabs, Tab },
  },
  theme: {
    ...getZudokuThemeConfig(activeBrand),
    customCss: `
      header a img {
        height: auto;
        max-height: 40px;
        width: auto;
      }
      @media (max-width: 640px) {
        header a img { max-height: 32px; }
      }
      /* Tablas: rejilla completa con bordes visibles */
      .prose table, .prose thead, .prose tbody, .prose tr, .prose th, .prose td {
        border-color: var(--border, #e2e8f0);
      }
      .prose table {
        border-collapse: collapse;
        width: 100%;
        border: 1px solid var(--border, #e2e8f0);
      }
      .prose th, .prose td {
        border: 1px solid var(--border, #e2e8f0);
        padding: 6px 12px;
        text-align: center;
      }
      .prose thead th {
        background: var(--muted, #f1f5f9);
        font-weight: 600;
      }
      /* Lateral: todo en mayúsculas */
      nav.scrollbar,
      nav.scrollbar a,
      nav.scrollbar button,
      nav.scrollbar span,
      nav.scrollbar div,
      nav.scrollbar li {
        text-transform: uppercase;
      }
      /* Item activo: fondo primary + texto secondary. Se excluye
         [data-anchor] porque todas las operaciones lo llevan y se
         resaltarían a la vez al activar su tag. */
      nav.scrollbar a[class*=" active"],
      nav.scrollbar div[class*=" active"],
      nav.scrollbar a[aria-current="page"]:not([data-anchor]),
      nav.scrollbar div[aria-current="page"]:not([data-anchor]) {
        background: var(--primary) !important;
        color: var(--secondary) !important;
      }
      nav.scrollbar a[class*=" active"]:hover,
      nav.scrollbar div[class*=" active"]:hover,
      nav.scrollbar a[aria-current="page"]:not([data-anchor]):hover,
      nav.scrollbar div[aria-current="page"]:not([data-anchor]):hover {
        background: var(--primary) !important;
        color: var(--secondary) !important;
      }
      /* Hijo activo del tag: texto secondary para legibilidad */
      nav.scrollbar a[class*=" active"] .text-primary,
      nav.scrollbar div[class*=" active"] .text-primary {
        color: var(--secondary) !important;
      }
      /* Cabeceras de categoría: texto secondary */
      nav.scrollbar span[class*="font-medium"] {
        color: var(--secondary) !important;
      }
      /* Operaciones hijas: react-router marca todo con aria-current, así
         que el único marcador fiable de selección es .bg-accent. */
      nav.scrollbar a[data-anchor].bg-accent,
      nav.scrollbar a[data-anchor].bg-accent * {
        color: var(--secondary) !important;
      }
      nav.scrollbar a[data-anchor]:not(.bg-accent),
      nav.scrollbar a[data-anchor]:not(.bg-accent) * {
        color: var(--foreground) !important;
      }
      /* Log In: botón sólido (incluye el icono, que Zudoku fuerza a muted). */
      header [data-slot="navigation-menu"] a[target="_blank"][href$="/"] {
        background: var(--primary) !important;
        color: var(--secondary) !important;
        border-radius: 6px;
        font-weight: 600;
        display: inline-flex !important;
        align-items: center;
        gap: 6px;
        padding: 6px 14px;
      }
      header [data-slot="navigation-menu"] a[target="_blank"][href$="/"]:hover {
        background: var(--primary) !important;
        color: var(--secondary) !important;
      }
      header [data-slot="navigation-menu"] a[target="_blank"][href$="/"] svg {
        color: var(--secondary) !important;
      }
      /* Insignia AUTH (punto verde pulsante + texto) */
      .api-auth-badge-container {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        margin-right: 8px;
        border-radius: 9999px;
        background: rgba(16, 185, 129, 0.12);
        border: 1px solid rgba(16, 185, 129, 0.35);
        color: #059669;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.05em;
        cursor: pointer;
        transition: all 0.2s ease;
        user-select: none;
      }
      .dark .api-auth-badge-container {
        background: rgba(16, 185, 129, 0.18);
        border-color: rgba(52, 211, 153, 0.4);
        color: #34d399;
      }
      .api-auth-badge-container:hover {
        background: rgba(16, 185, 129, 0.22);
        border-color: rgba(16, 185, 129, 0.6);
        transform: translateY(-1px);
      }
      .api-auth-pulse-dot {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 9px;
        height: 9px;
      }
      .api-auth-solid-dot {
        position: relative;
        width: 7px;
        height: 7px;
        border-radius: 9999px;
        background-color: #10b981;
      }
      .api-auth-pulse-ring {
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 9999px;
        background-color: #10b981;
        opacity: 0.75;
        animation: apiAuthPing 1.8s cubic-bezier(0, 0, 0.2, 1) infinite;
      }
      @keyframes apiAuthPing {
        75%, 100% {
          transform: scale(2.2);
          opacity: 0;
        }
      }
      .api-auth-text {
        line-height: 1;
      }
      /* Tema SweetAlert2 del modal AUTH */
      .swal2-container {
        z-index: 99999 !important;
      }
      .swal2-container:has(.api-auth-swal-popup) {
        backdrop-filter: blur(4px) !important;
      }
      .api-auth-swal-popup {
        border-radius: 14px !important;
        box-shadow: 0 16px 32px rgba(0, 0, 0, 0.12) !important;
        border: 1px solid var(--border, #e2e8f0) !important;
        padding: 18px 20px !important;
        font-family: inherit !important;
      }
      .api-auth-swal-close-btn {
        border-radius: 8px !important;
        font-weight: 600 !important;
        padding: 7px 16px !important;
        background: #f1f5f9 !important;
        color: #475569 !important;
        border: 1px solid #cbd5e1 !important;
        box-shadow: none !important;
      }
      .api-auth-swal-close-btn:hover {
        background: #e2e8f0 !important;
        color: #1e293b !important;
      }
      .api-auth-swal-danger-btn {
        border-radius: 8px !important;
        font-weight: 600 !important;
        padding: 7px 16px !important;
        background: #ef4444 !important;
        color: #ffffff !important;
        box-shadow: 0 1px 3px rgba(239, 68, 68, 0.3) !important;
      }
      .api-auth-swal-danger-btn:hover {
        background: #dc2626 !important;
      }
      /* Toast compacto */
      .api-auth-toast-popup {
        border-radius: 10px !important;
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08) !important;
        border: 1px solid var(--border, #e2e8f0) !important;
        padding: 10px 14px !important;
        font-size: 13px !important;
      }
      /* Selector de servidor como píldora estática solo con CSS (sin tocar
         el DOM de React, sin choques de hidratación). */
      .brand-server-parent-locked,
      div:has(> select[aria-label="Select server"]) {
        display: inline-flex !important;
        align-items: center !important;
      }
      select[aria-label="Select server"] {
        display: inline-block !important;
        pointer-events: none !important;
        cursor: default !important;
        border: 1px solid var(--border, #e2e8f0) !important;
        background: var(--muted, #f1f5f9) !important;
        color: var(--foreground) !important;
        font-family: monospace !important;
        font-size: 12px !important;
        padding: 4px 8px !important;
        border-radius: 6px !important;
        appearance: none !important;
        -webkit-appearance: none !important;
        -moz-appearance: none !important;
        outline: none !important;
        user-select: text !important;
      }
      .grid:has(> select[aria-label="Select server"]) > div {
        display: none !important;
      }
      /* Sign Up: botón fantasma, sólido solo en hover */
      header [data-slot="navigation-menu"] a[target="_blank"][href*="/register"] {
        border: 1px solid var(--primary) !important;
        color: var(--primary) !important;
        border-radius: 6px;
        font-weight: 600;
      }
      header [data-slot="navigation-menu"] a[target="_blank"][href*="/register"]:hover {
        background: var(--primary) !important;
        color: var(--secondary) !important;
      }
      /* Links del footer: el hover por defecto queda invisible; se fuerza legible. */
      footer li a {
        color: var(--muted-foreground);
      }
      footer li a:hover,
      footer li a:hover svg {
        color: var(--primary) !important;
      }
      footer li a:focus-visible {
        color: var(--primary) !important;
        outline-color: var(--ring);
      }
      /* Copyright del footer: primary con subrayado en hover. */
      .brand-footer-copyright:hover {
        text-decoration: underline;
      }
      /* Toggle del lateral: botón sólido de marca. */
      [data-sidebar-toggle] {
        border: none !important;
        background: var(--primary) !important;
        color: var(--secondary) !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        transition:
          transform 0.15s ease,
          box-shadow 0.15s ease;
      }
      [data-sidebar-toggle] svg {
        color: var(--secondary) !important;
      }
      [data-sidebar-toggle]:hover {
        transform: scale(1.08);
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.28);
        background: var(--primary) !important;
      }
      [data-sidebar-toggle]:focus-visible {
        outline: 2px solid var(--ring);
        outline-offset: 2px;
      }
      /* Chevron dibujado con pseudo-elemento (sin tocar el DOM de React). */
      [data-sidebar-toggle] svg {
        display: none !important;
      }
      [data-sidebar-toggle]::before {
        line-height: 1;
        font-size: 15px;
        font-weight: 700;
        letter-spacing: -1px;
        color: var(--secondary);
      }
      [data-sidebar-toggle][aria-expanded="true"]::before {
        content: "‹‹";
      }
      [data-sidebar-toggle][aria-expanded="false"]::before {
        content: "››";
      }
      /* Con el lateral colapsado, centra el contenido (Zudoku omite los márgenes). */
      html[data-sidebar-collapsed="true"] main .typography,
      body.sidebar-collapsed main .typography {
        margin-left: auto !important;
        margin-right: auto !important;
        width: 100%;
      }
      /* Mismo tamaño grande también expandido, centrado en la junta. */
      [data-sidebar-toggle][aria-expanded="true"] {
        width: 2.5rem !important;
        height: 2.5rem !important;
        left: calc(var(--side-nav-width) - 20px) !important;
      }
      /* Separador sobre la navegación prev/next inferior. */
      main div.h-16 + div.flex.justify-between {
        border-top: 1px solid var(--border);
        padding-top: 1.25rem;
        margin-top: 0.5rem;
      }
      /* Solo bordes lateral y de navbar; oculta divisores decorativos. */
      hr {
        border: none !important;
        background: transparent !important;
      }
      footer.bg-background {
        border-top: none !important;
      }
      div.h-px.bg-border,
      div.h-px[class*="bg-border"] {
        display: none !important;
      }
      /* Paginación prev/next: en hover, botón sólido de marca. */
      a.group[class*="p-5"] {
        border-radius: var(--radius);
      }
      a.group[class*="p-5"]:hover {
        background: var(--primary) !important;
        color: var(--secondary) !important;
        border-color: var(--primary);
      }
      a.group[class*="p-5"]:hover * {
        color: var(--secondary) !important;
      }
      a.group[class*="p-5"]:focus-visible {
        outline: 2px solid var(--ring);
        outline-offset: 1px;
      }
      /* Vistas previas de operaciones e imágenes de mensajes */
      .operation-preview-image,
      img[src*="/assets/api/"] {
        max-width: 340px !important;
        width: 100% !important;
        max-height: 520px !important;
        height: auto !important;
        object-fit: contain !important;
        border: 1px solid var(--border) !important;
        border-radius: 10px !important;
        display: block !important;
        margin: 16px 0 12px !important;
        background: var(--card) !important;
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06) !important;
      }
    `,
  },
    syntaxHighlighting: {
      themes: { light: "github-light", dark: "github-dark" },
      languages: [
        "json",
        "bash",
        "shell",
        "yaml",
        "javascript",
        "typescript",
        "http",
        "python",
        "java",
      ],
    },
  docs: { publishMarkdown: false },
  navigation: zudokuNavigation as unknown as ZudokuConfig["navigation"],
  redirects: [{ from: "/", to: "/introduction" }],
  slots: {
    "head-navigation-end": HostnameBrandSync,
  },
  apis: [
    {
      type: "file",
      input: "./apis/openapi.yaml",
      path: "/api",
      options: {
        showInfoPage: false,
      },
    },
  ],
};

export default config;